import { Box, Stack, Theme, useTheme } from '@mui/material';
import { Employee, TopicListItem } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { Localized, PoolChip, PoolTypography, ValueOrNDash } from '@synergy/components';
import { LockIcon } from '@synergy/theme';
import { PaloColumn, PaloListView, PaloSegmentedToggle } from '@synergy/components-palo';
import { PoolLink } from '@synergy/components-next';
import { getLocalizedString } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ChangeEmployeeTopicsModal } from './components/change-topics-modal';
import { CustomerTopicsEmpty } from './customer-topics-empty';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import { customerEmployeeTopicsSelector } from './store/customer-topics-selector';
import { fetchCustomerTopics, setEmployeeTopics } from './store/customer-topics-actions';
import { localesSelector } from '../../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        backgroundColor: 'white',
        '& tbody tr:last-child td': {
            paddingRight: 24,
        },
        marginBottom: theme.spacing(2),
    },
    chip: {
        marginLeft: 0,
        marginRight: theme.spacing(),
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        backgroundColor: '#F2F2F2',
    },
    dash: {
        marginLeft: theme.spacing(),
        color: theme.palette.grey[700],
    },
    addButton: {
        fontSize: 20,
    },
    disabledRow: {
        opacity: 0.5,
    },
}));

/** Read the same assignments from the person's side or the subject's side. */
type TopicsView = 'employee' | 'topic';

/**
 * Who is left once the filter has run. "Ohne Thema" is the one that finds
 * work: it names the people nobody has assigned a subject to yet.
 */
type TopicsFilter = 'all' | 'withTopics' | 'withoutTopics' | 'mainContact';

/** One subject and everyone who answers for it. */
interface TopicRow {
    id: number;
    title: string;
    employees: Employee[];
}

export const CustomerTopics = () => {
    const { id: customerIdParam } = useParams<{ id: string }>();
    const dispatch = useThunkDispatch();
    const employeeTopics = useAppSelector(customerEmployeeTopicsSelector);
    const locales = useAppSelector(localesSelector);
    const navigate = useNavigate();
    const theme = useTheme();
    const intl = useIntl();
    const classes = useStyles();
    const [changeTopicsModalData, setChangeTopicsModalData] = useState<Employee | null>(null);
    const [view, setView] = useState<TopicsView>('employee');
    const [filter, setFilter] = useState<TopicsFilter>('all');
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (customerIdParam) {
            dispatch(fetchCustomerTopics(+customerIdParam));
        }
    }, [customerIdParam, dispatch]);

    const titleOf = useMemo(
        () => (topic: { title: Parameters<typeof getLocalizedString>[1] }) => getLocalizedString(locales, topic.title),
        [locales]
    );

    // Someone who has left is still on the list, only out of play.
    const sorted = useMemo(
        () =>
            [...(employeeTopics ?? [])].sort((a, b) =>
                a.isLocked === b.isLocked ? a.firstName.localeCompare(b.firstName) : a.isLocked ? 1 : -1
            ),
        [employeeTopics]
    );

    const employeeRows = useMemo(() => {
        const needle = search.trim().toLowerCase();

        return sorted
            .filter(employee => {
                switch (filter) {
                    case 'withTopics':
                        return (employee.topics?.length ?? 0) > 0;
                    case 'withoutTopics':
                        return 0 === (employee.topics?.length ?? 0);
                    case 'mainContact':
                        return !!employee.isMainContact;
                    default:
                        return true;
                }
            })
            .filter(employee => {
                if (!needle) {
                    return true;
                }

                // The search reaches both sides of the assignment, so a subject
                // finds its people and a name finds its subjects.
                return (
                    employee.fullName.toLowerCase().includes(needle) ||
                    (employee.topics ?? []).some(topic => titleOf(topic).toLowerCase().includes(needle))
                );
            });
    }, [sorted, filter, search, titleOf]);

    const topicRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const byTopic = new Map<number, TopicRow>();

        sorted.forEach(employee =>
            (employee.topics ?? []).forEach(topic => {
                const row = byTopic.get(topic.id) ?? { id: topic.id, title: titleOf(topic), employees: [] };

                row.employees.push(employee);
                byTopic.set(topic.id, row);
            })
        );

        return [...byTopic.values()]
            .filter(row => {
                if ('mainContact' === filter) {
                    return row.employees.some(employee => employee.isMainContact);
                }

                return true;
            })
            .filter(
                row =>
                    !needle ||
                    row.title.toLowerCase().includes(needle) ||
                    row.employees.some(employee => employee.fullName.toLowerCase().includes(needle))
            )
            .sort((a, b) => a.title.localeCompare(b.title));
    }, [sorted, filter, search, titleOf]);

    if (!employeeTopics || 0 === employeeTopics.length) {
        return <CustomerTopicsEmpty />;
    }

    const handleEdit = (employeeId: number) => {
        navigate(ROUTES.contactsAndOrganizations.contacts.detail.replace(':id', employeeId.toString()));
    };

    const handleChangeTopics = (employee: Employee | null, topics: TopicListItem[]) => {
        if (employee && customerIdParam) {
            dispatch(setEmployeeTopics(+customerIdParam, employee.id, topics));
        }
        setChangeTopicsModalData(null);
    };

    const employeeColumns: PaloColumn<Employee>[] = [
        {
            key: 'name',
            type: 'name',
            head: <FormattedMessage id="app.table.column.name" />,
            cell: employee => (
                <>
                    <PoolTypography component="span" typography="body1">
                        <Stack direction="row" spacing={1} alignItems="center">
                            {employee.isLocked && <LockIcon style={{ fontSize: 16 }} />}
                            <Box>{employee.fullName}</Box>
                        </Stack>
                    </PoolTypography>
                    <PoolTypography color="grey.700" typography="caption" component="div">
                        <ValueOrNDash>
                            <Localized value={employee.position?.name} />
                        </ValueOrNDash>
                    </PoolTypography>
                    <PoolTypography typography="caption" component="div" color="grey.700">
                        <PoolLink
                            href={`mailto:${employee.email}`}
                            colorVariant="grey700"
                            hoverColor={theme.palette.primary.main}
                            onClick={e => e.stopPropagation()}
                        >
                            {employee.email}
                        </PoolLink>
                    </PoolTypography>
                </>
            ),
        },
        {
            key: 'mainContact',
            type: 'name',
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.mainContact" />,
            cell: employee =>
                employee.isMainContact ? (
                    <PoolTypography
                        typography="caption"
                        component="span"
                        id="app.contactsAndOrganizations.detail.mainContact"
                        color="primary"
                    />
                ) : null,
        },
        {
            key: 'topics',
            width: '40%',
            wrap: true,
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.topics" />,
            cell: employee => (
                <ValueOrNDash dashProps={{ className: classes.dash }}>
                    {employee.topics?.map(topic => (
                        <PoolChip key={topic.id} className={classes.chip} label={titleOf(topic)} type="tag" />
                    ))}
                </ValueOrNDash>
            ),
        },
    ];

    const topicColumns: PaloColumn<TopicRow>[] = [
        {
            key: 'topic',
            type: 'name',
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.topics" />,
            cell: row => row.title,
        },
        {
            key: 'employees',
            type: 'name',
            wrap: true,
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.tab.employees" />,
            cell: row => (
                <ValueOrNDash dashProps={{ className: classes.dash }}>
                    {row.employees.map(employee => (
                        <PoolChip
                            key={employee.id}
                            className={classes.chip}
                            label={employee.isMainContact ? `${employee.fullName} ★` : employee.fullName}
                            type="tag"
                        />
                    ))}
                </ValueOrNDash>
            ),
        },
        {
            key: 'count',
            type: 'count',
            head: <FormattedMessage id="app.table.column.count" />,
            cell: row => row.employees.length,
        },
    ];

    const controls = (
        <Stack direction="row" gap={1.5} alignItems="center">
            <PaloSegmentedToggle
                value={view}
                onChange={value => setView(value as TopicsView)}
                options={[
                    {
                        value: 'employee',
                        label: <FormattedMessage id="app.contactsAndOrganizations.detail.tab.employees" />,
                    },
                    { value: 'topic', label: <FormattedMessage id="app.contactsAndOrganizations.detail.topics" /> },
                ]}
            />
            <PaloSegmentedToggle
                value={filter}
                onChange={value => setFilter(value as TopicsFilter)}
                options={[
                    { value: 'all', label: <FormattedMessage id="app.common.all" /> },
                    { value: 'withTopics', label: <FormattedMessage id="app.topics.filter.withTopics" /> },
                    { value: 'withoutTopics', label: <FormattedMessage id="app.topics.filter.withoutTopics" /> },
                    {
                        value: 'mainContact',
                        label: <FormattedMessage id="app.contactsAndOrganizations.detail.mainContact" />,
                    },
                ]}
            />
        </Stack>
    );

    const searchProps = {
        searchValue: search,
        onSearchChange: setSearch,
        searchPlaceholder: intl.formatMessage({ id: 'app.topics.searchPlaceholder' }),
    };

    return (
        <>
            {'employee' === view ? (
                <PaloListView<Employee, number>
                    embedded={true}
                    headerActions={controls}
                    columns={employeeColumns}
                    items={employeeRows}
                    getRowId={employee => employee.id}
                    emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                    isRowMuted={employee => employee.isLocked}
                    onRowClick={employee => handleEdit(employee.id)}
                    rowHref={employee =>
                        ROUTES.contactsAndOrganizations.contacts.detail.replace(':id', employee.id.toString())
                    }
                    rowActions={employee => [
                        {
                            label: <FormattedMessage id="app.contactsAndOrganizations.detail.changeTopics" />,
                            onClick: () => setChangeTopicsModalData(employee),
                        },
                    ]}
                    {...searchProps}
                />
            ) : (
                <PaloListView<TopicRow, number>
                    embedded={true}
                    headerActions={controls}
                    columns={topicColumns}
                    items={topicRows}
                    getRowId={row => row.id}
                    emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                    {...searchProps}
                />
            )}
            <ChangeEmployeeTopicsModal
                isVisible={!!changeTopicsModalData}
                topics={changeTopicsModalData?.topics}
                onCancel={() => setChangeTopicsModalData(null)}
                onConfirm={topics => handleChangeTopics(changeTopicsModalData, topics)}
            />
        </>
    );
};
